export const formatConditionForDisplay = (condition) => {
  if (!condition || typeof condition !== "string") return condition;
  const parsed = parseCustomFieldFromCondition(condition);
  if (parsed) {
    return `${parsed.field}=${parsed.value}`;
  }
  return condition;
};

export const normalizeCustomCondition = (condition) => {
  if (!condition || typeof condition !== "string") return "";
  return condition.trim().replace(/\s+/g, "").toUpperCase();
};

export const parseCustomFieldFromCondition = (condition) => {
  if (!condition || typeof condition !== "string") return null;
  const trimmed = condition.trim();
  if (!/^custom/i.test(trimmed)) return null;

  const structuredMatch = trimmed.match(/^custom\s*=\s*([^=]+)\s*=\s*(.+)$/i);
  if (!structuredMatch) return null;

  const field = structuredMatch[1].trim();
  const value = structuredMatch[2].trim();
  return {
    field,
    value,
    condition: `CUSTOM=${field}=${value}`,
  };
};

const getRowConditions = (row) => {
  if (Array.isArray(row)) return row;
  return row?.conditions || [];
};

const getSelectionMap = (productCustomOptions = []) => {
  const selections = {};
  for (const option of productCustomOptions) {
    const parsed = parseCustomFieldFromCondition(option);
    if (!parsed) continue;
    selections[parsed.field.toUpperCase()] = parsed.value;
  }
  return selections;
};

const rowMatchesOtherSelections = (rowConditions, fieldName, selections) =>
  rowConditions.every((condition) => {
    const parsed = parseCustomFieldFromCondition(condition);
    if (!parsed) return true;

    const fieldKey = parsed.field.toUpperCase();
    if (fieldKey === String(fieldName).trim().toUpperCase()) return true;

    const selected = selections[fieldKey];
    if (!selected) return true;

    return selected === parsed.value;
  });

export const filterCustomFieldsBySelections = (
  fields = [],
  rows = [],
  productCustomOptions = []
) => {
  if (!rows.length) return fields;

  const allSelections = getSelectionMap(productCustomOptions);

  return fields.map((field, fieldIndex) => {
    const priorFieldKeys = new Set(
      fields
        .slice(0, fieldIndex)
        .map((item) => String(item.name).trim().toUpperCase())
    );
    const priorSelections = {};
    for (const [key, value] of Object.entries(allSelections)) {
      if (priorFieldKeys.has(key)) {
        priorSelections[key] = value;
      }
    }

    const eligibleRows = rows.filter((row) =>
      rowMatchesOtherSelections(
        getRowConditions(row),
        field.name,
        priorSelections
      )
    );

    const optionMap = new Map();
    for (const row of eligibleRows) {
      for (const condition of getRowConditions(row)) {
        const parsed = parseCustomFieldFromCondition(condition);
        if (!parsed) continue;
        if (parsed.field.toUpperCase() !== String(field.name).trim().toUpperCase()) {
          continue;
        }

        const key = normalizeCustomCondition(parsed.condition);
        if (optionMap.has(key)) continue;

        const existing = field.options?.find(
          (option) => normalizeCustomCondition(option.condition) === key
        );
        optionMap.set(
          key,
          existing || {
            label: parsed.value,
            value: parsed.value,
            condition: parsed.condition,
          }
        );
      }
    }

    const options = [...optionMap.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), undefined, {
        numeric: true,
      })
    );

    return { ...field, options };
  });
};

export const pruneInvalidCustomSelections = (
  fields = [],
  rows = [],
  productCustomOptions = []
) => {
  let nextOptions = [...(productCustomOptions || [])];

  for (const field of [...fields].reverse()) {
    const selectedValue = getSelectedCustomValue(nextOptions, field.name);
    if (!selectedValue) continue;

    const filteredFields = filterCustomFieldsBySelections(
      fields,
      rows,
      nextOptions
    );
    const allowed = filteredFields.find((item) => item.name === field.name);
    const stillValid = allowed?.options?.some(
      (option) => option.value === selectedValue
    );

    if (!stillValid) {
      nextOptions = updateProductCustomOptions(nextOptions, field.name, null);
    }
  }

  return nextOptions;
};

export const getSelectedCustomCondition = (productCustomOptions, fieldName) => {
  const options = productCustomOptions || [];
  const match = options.find((option) => {
    const normalized = normalizeCustomCondition(option);
    const prefix = `CUSTOM=${String(fieldName).trim().toUpperCase()}=`;
    return normalized.startsWith(prefix);
  });
  return match || null;
};

export const getSelectedCustomValue = (productCustomOptions, fieldName) => {
  const condition = getSelectedCustomCondition(productCustomOptions, fieldName);
  if (!condition) return null;

  const normalized = normalizeCustomCondition(condition);
  const prefix = `CUSTOM=${String(fieldName).trim().toUpperCase()}=`;
  if (!normalized.startsWith(prefix)) return null;
  return normalized.slice(prefix.length);
};

export const updateProductCustomOptions = (
  currentOptions,
  fieldName,
  selectedOption
) => {
  const list = [...(currentOptions || [])];
  const fieldKey = String(fieldName).trim().toUpperCase();
  const filtered = list.filter((option) => {
    const normalized = normalizeCustomCondition(option);
    const prefix = `CUSTOM=${fieldKey}=`;
    return !normalized.startsWith(prefix);
  });

  if (selectedOption?.condition) {
    filtered.push(selectedOption.condition);
  }

  return filtered;
};

export const updateLegacyCustomOption = (currentOptions, condition) => {
  const list = [...(currentOptions || [])];
  const filtered = list.filter(
    (option) => !/^custom\s*=/i.test(String(option).trim())
  );
  if (condition) {
    filtered.push(condition);
  }
  return filtered;
};
